import React from 'react'
import { BarChart3, Check, ShoppingBag, Store } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { useGetToolInterestQuery, useSetToolInterestMutation } from '@/redux/marketerApi'

/**
 * A tool we haven't built. "I'm interested" records that this brand would connect it, so the
 * team builds the ones brands actually ask for; tapping again takes it back.
 */
const ToolRow: React.FC<{
  id: string
  icon: React.ReactNode
  title: string
  blurb: string
  interested: boolean
  busy: boolean
  onToggle: (id: string, interested: boolean) => void
}> = ({ id, icon, title, blurb, interested, busy, onToggle }) => (
  <li className="flex flex-col gap-3 rounded-xl border border-foreground/10 bg-card p-4 sm:flex-row sm:items-center shadow-[0_1px_2px_rgba(30,41,59,0.04)]">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-foreground">{title}</span>
        <Badge className="bg-muted text-gray-700 hover:bg-foreground/10">Coming soon</Badge>
      </span>
      <span className="block text-sm text-gray-600">{blurb}</span>
    </span>
    <Button
      variant={interested ? 'ghost' : 'dark-outline'}
      size="sm"
      disabled={busy}
      aria-pressed={interested}
      onClick={() => onToggle(id, !interested)}
      className={cn('self-start sm:self-center', interested && 'text-[#3F7A0A]')}
    >
      {interested ? (
        <>
          <Check className="h-4 w-4" /> We'll let you know
        </>
      ) : (
        "I'm interested"
      )}
    </Button>
  </li>
)

/**
 * Store and site tools brands could connect (Shopify, Google Analytics, TikTok Shop). None are
 * built yet; "I'm interested" counts demand so the team builds the ones brands ask for.
 */
export const ToolInterest: React.FC = () => {
  const { toast } = useToast()
  const { data: interest } = useGetToolInterestQuery()
  const [setInterest, { isLoading }] = useSetToolInterestMutation()
  const wanted = interest?.tools ?? []
  const toggle = async (tool: string, on: boolean) => {
    try {
      await setInterest({ tool, interested: on }).unwrap()
      if (on) toast({ title: 'Thanks, noted', description: "We'll tell you when it's ready." })
    } catch {
      toast({
        title: "Couldn't save that",
        description: 'Try again in a moment.',
        variant: 'destructive',
      })
    }
  }
  return (
    <section>
      <h2 className="text-lg font-bold text-foreground">Tools</h2>
      <p className="text-sm text-gray-600">
        Connect your store and site to see what athletes drive, next to each deal. Post performance
        comes from the athletes' own connected accounts. Tell us which ones you'd use and we'll
        build those first.
      </p>
      <ul className="mt-4 space-y-3">
        <ToolRow
          id="shopify"
          interested={wanted.includes('shopify')}
          busy={isLoading}
          onToggle={toggle}
          icon={<ShoppingBag className="h-5 w-5" />}
          title="Shopify"
          blurb="Give each athlete a code and watch the orders come in."
        />
        <ToolRow
          id="google_analytics"
          interested={wanted.includes('google_analytics')}
          busy={isLoading}
          onToggle={toggle}
          icon={<BarChart3 className="h-5 w-5" />}
          title="Google Analytics"
          blurb="Traffic from athlete links, on your results page."
        />
        <ToolRow
          id="tiktok_shop"
          interested={wanted.includes('tiktok_shop')}
          busy={isLoading}
          onToggle={toggle}
          icon={<Store className="h-5 w-5" />}
          title="TikTok Shop"
          blurb="Affiliate links for the content athletes post."
        />
      </ul>
    </section>
  )
}

export default ToolInterest
